import axios from "axios";
import featureFlags from "*external*/core/plugin/featureFlags";
import { auth } from "./PlatformAuth";
import * as Config from "./data/Config";

const login_url = `${window.location.origin}/app/idm/auth/local`;

const sendToLogin = (state = {}, scope = auth.authType, email) => {
   const params = new URLSearchParams({
      state: JSON.stringify(state),
   });

   if (email) {
      params.set("email", email);
   }

   if (auth.authType === "IDM:SSO:Local") {
      params.set("loginurl", login_url);
   }

   const url = `${auth.authorizePath}&scope=${scope}&${params.toString()}`;
   window.location = url;
};

const sendToHome = () => window.location.assign("/");

const logoutPath = (type = "normal") =>
   `${auth.logoutPath}${encodeURIComponent(`?logout=type:${type}`)}`;

/**
 * @deprecated migrate to `Auth.logout()`
 */
async function logout(invalidAccess = false, type = "normal") {
   invalidAccess && sessionStorage.setItem("invalidAccess", invalidAccess);
   try {
      await axios.get(`${Config.get().base_path}checkSession`);
      return logoutPath(type);
   } catch {
      window.location = "/auth/login";
   }
}

const selectTenant = (tenantId) =>
   axios.post(
      `${Config.get().base_path}auth/selectTenant?tenantId=${tenantId}`
   );

const getLauthUsers = () => axios.get(`${auth.cdnaUrl}/api/idm/v1/local/users`);

const createLauthUser = (payload) =>
   axios.post(`${auth.cdnaUrl}/api/idm/v1/local/users`, payload);

const deleteLauthUser = (userId) =>
   axios.delete(`${auth.cdnaUrl}/api/idm/v1/local/users/${userId}`);

const getRoles = () =>
   axios.get(
      `${auth.cdnaUrl}/idm/api/v1/im/tenants/${auth.tenantId}/roles`,
      timeout
   );

const getRDs = () => axios.get(`${auth.cdnaUrl}/idm/api/v1/im/rds`);

const getFirstLogin = () => axios.get("/auth/firstLogin");

const updateFirstLogin = () => axios.post("/auth/firstLogin");

// Associate the configured offer id with the given tenant
// Currently a platform-ui instance serves a single offer
const addApplication = (tenantId) => {
   const { cdna_url, region = "us-west-2", offer_id } = Config.get();
   return axios.put(
      `${cdna_url}/idm/api/v1/im/tenants/${tenantId}/apps/${offer_id}`,
      { regions: [region], misc: [] }
   );
};

const accountNameRegex = /^.{3,100}$/; // no spaces, length between 3 and 100
const isValidAccountName = (name) => name && accountNameRegex.exec(name);

/**
 *
 * @returns {boolean}
 */
const EN_enabled = () =>
   featureFlags.isEnabled("cisco.dna.core.system/enable-EN-Portal") ||
   auth.enEnabled;

/**
 * Update the current user's profile to denote the fact that they
 * have logged in at least once.
 *
 * Prior to this, the user will typically have a status of "invited",
 * unless it's the user who initially created the account (tenant).
 */
export async function updateLoginStatus() {
   if (Config.get().auth_skip) {
      return;
   }

   const {
      cdnaUrl,
      tenantId,
      userId,
      email,
      firstName,
      lastName,
      roles,
      userName,
   } = auth;

   try {
      const resp = await axios.get(
         `/auth/checkUser?userId=${auth.userId}&tenantId=${
            auth.tenantId
         }&EN_enabled=${EN_enabled()}`
      );
      const { misc } = resp.data;

      // if status is already enabled, no need to enable it again
      const statusInfo = (misc || []).find(
         (value) => value.type === "platform-ui/status"
      );
      if (statusInfo && statusInfo.value && statusInfo.value[0] === "enabled") {
         return;
      }
      const enabledStatus = {
         type: "platform-ui/status",
         value: ["enabled"],
      };
      const newMisc = misc
         ? [
              ...misc.filter((obj) => obj.type !== "platform-ui/status"),
              enabledStatus,
           ]
         : [enabledStatus];
      const payload = {
         email,
         firstName,
         lastName,
         misc: newMisc,
         roles,
         username: userName,
      };
      return axios.put(
         `${cdnaUrl}/idm/api/v1/im/tenants/${tenantId}/users/${userId}`,
         payload
      );
   } catch {
      try {
         await axios.get(`${Config.get().base_path}checkSession`);
         window.location = logoutPath("invalidRegion");
      } catch {
         window.location = "/auth/login";
      }
   }
}

export {
   auth,
   sendToLogin,
   sendToHome,
   selectTenant,
   getLauthUsers,
   createLauthUser,
   deleteLauthUser,
   getRoles,
   getRDs,
   getFirstLogin,
   updateFirstLogin,
   addApplication,
   logout,
   logoutPath,
   isValidAccountName,
   accountNameRegex,
   EN_enabled,
};

/**
 * Caches the result of a promise for `ttl` time (in milliseconds)
 *
 * Passe in `true` to force call the function
 *
 * @param {*} promiseFn
 * @param {number} ttl The time till the cache is invalid
 * @returns {(forceRefresh?: boolean)=>Promise<any>}
 */
export function memoize(promiseFn, ttl) {
   let lastFetch = 0;
   let result = null;

   return (forceRefresh) => {
      const timeSince = Date.now() - lastFetch;

      if (result && timeSince <= ttl && !forceRefresh) {
         return result;
      }

      lastFetch = Date.now();
      result = promiseFn();

      return result;
   };
}

/**
 * A promise'd version of setTimeout
 *
 * @param {number} ms How long the timeout should be
 */
export const timeout = (ms) =>
   new Promise((resolve) => setTimeout(() => resolve(), ms));

/**
 * Casts an error into an axios error.
 *
 * @param {unknown} error
 * @returns {error is import("axios").AxiosError}
 */
export const isAxiosError = (error) =>
   error && error.response && error.response.status;

/**
 * Don't do anything.
 */
export const ignore = () => {
   /** noop */
};
